import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {UserCaregiverForm} from '../../../../../../../components/user-form/user-caregiver-form';
import {useOffice} from '../../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {CaregiverMode} from '../../../../../../../enums';
import {UserFormType} from '../../../../../../../enums/user-form-type';
import {translations} from '../../../../../../../i18n/translations';
import {EntitiesRepository} from '../../../../../../../repositories/entities';
import {UsersRepository} from '../../../../../../../repositories/users';

export default function CaregiverProfileSystemAdministratorOfficeScreen({
  caregiverMode,
}: {
  caregiverMode: CaregiverMode.SystemAdministrator | CaregiverMode.Professional;
}) {
  // Get params
  let {id, entityId}: {id?: string; entityId?: string} = useParams();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get context
  const snackBarContext = useSnackBar();
  const officeContext = useOffice();

  // Validate and get the user
  useEffect(() => {
    const getUser = async () => {
      if (id) {
        try {
          if (caregiverMode === CaregiverMode.SystemAdministrator) {
            // Get user
            const userDetails = await UsersRepository.instance.getById(id);

            if (userDetails) {
              // Set user for edit/view
              officeContext.setUserForm(userDetails);
            } else {
              // Error
              throw new Error('Empty user.');
            }
          } else {
            // Check entity id
            if (!entityId) {
              // Error
              throw new Error('Empty entity id.');
            }

            // Get user and affiliation
            const userAffiliationDetails = await EntitiesRepository.instance.getAffiliation(
              entityId,
              id
            );

            if (userAffiliationDetails) {
              // Set user for edit/view
              officeContext.setUserForm(
                userAffiliationDetails.user,
                userAffiliationDetails.affiliation
              );
            } else {
              // Error
              throw new Error('Empty user.');
            }
          }
        } catch (error) {
          // Log
          console.debug('🔴 Could not get user.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.caregiver_tabs.errors.caregiver_does_not_exist_error),
          });

          // Redirect
          history.goBack();
        }
      } else {
        if (caregiverMode === CaregiverMode.SystemAdministrator) {
          // Log
          console.debug('🔴 Could not get user.');

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.caregiver_tabs.errors.caregiver_does_not_exist_error),
          });

          // Redirect
          history.goBack();
        }
        // Invite screen
      }
    };

    // Get user
    getUser();

    // Clean up
    return () => {
      // Clear user
      officeContext.clearUserForm();

      // Log
      console.debug('🧹 Caregiver form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <UserCaregiverForm
      userFormType={UserFormType.EditCaregiver_SystemAdministrator}
      redirectUrl={`${url.replace(`/${id}`, '')}`}
    />
  );
}
